'use client';

import { useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import Box from '../../../atoms/Box/Box';
import Iconography from '../../../atoms/Iconography/Iconography';
import Typography from '../../../atoms/Typography/Typography';
import type { GroupField, FormField } from '../types';
import type { FieldValues } from 'react-hook-form';
import { getEffectiveWidth } from '../utils';
import styles from '../Form.module.scss';

const TRANSITION_DURATION_MS = 300;

export type GroupControlProps = {
  field: GroupField;
  theme: 'dark' | 'light';
  watchedValues: FieldValues;
  renderField: (field: FormField, index: number) => React.ReactNode;
};

/**
 * Form control for grouped fields with optional title and description.
 * Supports collapsible behavior when `field.collapsible` is true.
 * @component
 * @param {GroupControlProps} props - Component props
 * @returns {JSX.Element} Group control component
 */
const GroupControl = ({ field, theme, watchedValues, renderField }: GroupControlProps) => {
  const isFlat = field.variant === 'flat';
  const isCollapsible = field.collapsible === true;
  const defaultCollapsed = isCollapsible && field.defaultCollapsed === true;
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [overflowVisible, setOverflowVisible] = useState(!defaultCollapsed);
  const gridOuterRef = useRef<HTMLDivElement>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const handleToggle = () => {
    if (!isCollapsible) return;

    const isExpanding = collapsed;
    setCollapsed((prev) => !prev);

    if (isExpanding) {
      // Expanding: keep overflow hidden during animation, reveal after
      setOverflowVisible(false);

      const onEnd = (e: TransitionEvent) => {
        if (e.propertyName === 'grid-template-rows') {
          setOverflowVisible(true);
          clearFallbackTimer();
          gridOuterRef.current?.removeEventListener('transitionend', onEnd);
          gridOuterRef.current?.removeEventListener('transitioncancel', onCancel);
        }
      };

      const onCancel = () => {
        setOverflowVisible(true);
        clearFallbackTimer();
        gridOuterRef.current?.removeEventListener('transitionend', onEnd);
        gridOuterRef.current?.removeEventListener('transitioncancel', onCancel);
      };

      gridOuterRef.current?.addEventListener('transitionend', onEnd);
      gridOuterRef.current?.addEventListener('transitioncancel', onCancel);

      // Fallback in case transitionend doesn't fire (e.g. tab hidden)
      clearFallbackTimer();
      fallbackTimerRef.current = setTimeout(() => {
        setOverflowVisible(true);
        gridOuterRef.current?.removeEventListener('transitionend', onEnd);
        gridOuterRef.current?.removeEventListener('transitioncancel', onCancel);
      }, TRANSITION_DURATION_MS + 50);
    } else {
      // Collapsing: immediately hide overflow so content clips during animation
      setOverflowVisible(false);
      clearFallbackTimer();
    }
  };

  return (
    <Box
      key={`form_field_group_${field.id}`}
      className={classnames(styles.groupContainer, {
        [styles.groupContainerCollapsed]: collapsed,
        [styles.groupContainerFlat]: isFlat,
      })}
      data-theme={theme}
      data-group-collapsed={isCollapsible ? collapsed : undefined}
    >
      {(field.title || field.description) && (
        <Box
          className={classnames(styles.groupHeader, {
            [styles.groupHeaderCollapsible]: isCollapsible,
            [styles.groupHeaderCollapsed]: collapsed,
          })}
          onClick={isCollapsible ? handleToggle : undefined}
          data-testid={isCollapsible ? `group-toggle-${field.id}` : undefined}
        >
          <Box className={styles.groupHeaderContent}>
            {field.title && (
              <Typography
                variant="subtitle"
                size="small"
                className={styles.groupTitle}
                color={theme === 'dark' ? 'black' : 'white'}
                theme={theme}
              >
                {field.title}
              </Typography>
            )}
            {field.description && (
              <Typography
                variant="body"
                size="small"
                theme={theme}
                color="gray-5"
                className={styles.groupDescription}
              >
                {field.description}
              </Typography>
            )}
          </Box>
          {isCollapsible && (
            <Box
              className={classnames(styles.groupChevron, {
                [styles.groupChevronCollapsed]: collapsed,
              })}
            >
              <Iconography
                category="arrows"
                name="chevron-up"
                color={theme === 'dark' ? 'white' : 'black'}
              />
            </Box>
          )}
        </Box>
      )}
      <Box
        ref={gridOuterRef}
        className={classnames(styles.groupGridOuter, {
          [styles.groupGridOuterCollapsed]: collapsed,
        })}
      >
        <Box
          className={classnames(styles.groupGrid, {
            [styles.groupGridOverflowVisible]: overflowVisible,
          })}
        >
          {field.children
            .filter((childField) => childField.visible !== false)
            .map((childField, childIndex) => {
              const effectiveWidth = getEffectiveWidth({
                field: childField,
                watchedValues,
              });
              return (
                <Box
                  key={childField.id}
                  className={classnames(styles.wrapper, styles[effectiveWidth])}
                >
                  {renderField(childField as FormField, childIndex)}
                </Box>
              );
            })}
        </Box>
      </Box>
    </Box>
  );
};

export default GroupControl;
